import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { CSRPCustomerBillingGroupType, CSRPRouteType } from '../../types/csrp/csrp.type';
import {
  deletePRIDetails,
  getBillingGroupsList,
  getPRIDetails,
  getPRIList,
  getRoutesList,
  patchPRIDetails,
  patchPriDnisPrefix,
  postImportDIDsFormPRI,
  postImportPRI,
  postPRIDetails,
} from './csrp.action';
import { PRIType } from '../../types/pri/pri.type';
import { getSettings } from '../../utils/utilities';

const settings = getSettings();
export interface CSRPState {
  priList: null | PaginationType<CSRPCustomerBillingGroupType>;
  priListFetchStatus: FETCH_STATUS;
  priRouteParams: RouteParamsType;
  billingGroupsRouteParams: RouteParamsType;
  billingGroupsList: null | PaginationType<CSRPCustomerBillingGroupType>;
  billingGroupsListFetchStatus: FETCH_STATUS;
  importPRIPostStatus: FETCH_STATUS;
  importDIDsFromPRIPostStatus: FETCH_STATUS;
  priDetails: null | PRIType;
  priDetailsFetchStatus: FETCH_STATUS;
  priDetailsPostStatus: FETCH_STATUS;
  priDetailsPatchStatus: FETCH_STATUS;
  priDetailsDeleteStatus: FETCH_STATUS;
  priDnisPrefixPatchStatus: FETCH_STATUS;
  routesRouteParams: RouteParamsType;
  routesList: null | PaginationType<CSRPRouteType>;
  routesListFetchStatus: FETCH_STATUS;
}

export const initialPRIRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-obj.id',
  q: '',
  esi_id: '',
  customer_id: '',
};

export const initialBillingGroupsRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-obj.id',
  q: '',
};

export const initialRouteRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-obj.id',
  q: '',
};

const INIT_STATE: CSRPState = {
  priRouteParams: initialPRIRouteParams,
  priList: null,
  priListFetchStatus: FETCH_STATUS.NULL,
  billingGroupsList: null,
  billingGroupsListFetchStatus: FETCH_STATUS.NULL,
  billingGroupsRouteParams: initialBillingGroupsRouteParams,
  importPRIPostStatus: FETCH_STATUS.NULL,
  importDIDsFromPRIPostStatus: FETCH_STATUS.NULL,
  priDetails: null,
  priDetailsFetchStatus: FETCH_STATUS.NULL,
  priDetailsPostStatus: FETCH_STATUS.NULL,
  priDetailsPatchStatus: FETCH_STATUS.NULL,
  priDetailsDeleteStatus: FETCH_STATUS.NULL,
  priDnisPrefixPatchStatus: FETCH_STATUS.NULL,
  routesListFetchStatus: FETCH_STATUS.NULL,
  routesList: null,
  routesRouteParams: initialRouteRouteParams,
};

const csrpSlice = createSlice({
  name: 'csrp',
  initialState: INIT_STATE,
  reducers: {
    setPriList: (state, action) => {
      state.priList = action.payload;
    },
    setPriListFetchStatus: (state, action) => {
      state.priListFetchStatus = action.payload;
    },
    setBillingGroupsList: (state, action) => {
      state.billingGroupsList = action.payload;
    },
    setBillingGroupsListFetchStatus: (state, action) => {
      state.billingGroupsListFetchStatus = action.payload;
    },
    setRoutesList: (state, action) => {
      state.routesList = action.payload;
    },
    setRoutesListFetchStatus: (state, action) => {
      state.routesListFetchStatus = action.payload;
    },
    setImportPRIPostStatus: (state, action) => {
      state.importPRIPostStatus = action.payload;
    },
    setImportDIDsFromPRIPostStatus: (state, action) => {
      state.importDIDsFromPRIPostStatus = action.payload;
    },
    setPRIDetails: (state, action) => {
      state.priDetails = action.payload;
    },
    setPRIDetailsFetchStatus: (state, action) => {
      state.priDetailsFetchStatus = action.payload;
    },
    setPRIDetailsPostStatus: (state, action) => {
      state.priDetailsPostStatus = action.payload;
    },
    setPRIDetailsPatchStatus: (state, action) => {
      state.priDetailsPatchStatus = action.payload;
    },
    setPRIDetailsDeleteStatus: (state, action) => {
      state.priDetailsDeleteStatus = action.payload;
    },
    setPriDnisPrefixPatchStatus: (state, action) => {
      state.priDnisPrefixPatchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'pri') {
        state.priRouteParams = { ...state.priRouteParams, ...payload.data };
      }

      if (payload.reducer === 'billing-groups') {
        state.billingGroupsRouteParams = { ...state.billingGroupsRouteParams, ...payload.data };
      }

      if (payload.reducer === 'csrp-routes') {
        state.routesRouteParams = { ...state.routesRouteParams, ...payload.data };
      }
    });

    // GET MEGASWITCH DOMAINS LIST
    builder.addCase(getPRIList.pending, state => {
      state.priListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPRIList.rejected, (state, action) => {
      if (action.payload) {
        state.priListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getPRIList.fulfilled, (state, action) => {
      state.priListFetchStatus = FETCH_STATUS.FULFILLED;
      state.priList = action.payload;
    });

    // GET BILLING GROUPS LIST
    builder.addCase(getBillingGroupsList.pending, state => {
      state.billingGroupsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getBillingGroupsList.rejected, (state, action) => {
      if (action.payload) {
        state.billingGroupsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getBillingGroupsList.fulfilled, (state, action) => {
      state.billingGroupsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.billingGroupsList = action.payload;
    });

    // GET ROUTES LIST
    builder.addCase(getRoutesList.pending, state => {
      state.routesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getRoutesList.rejected, (state, action) => {
      if (action.payload) {
        state.routesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getRoutesList.fulfilled, (state, action) => {
      state.routesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.routesList = action.payload;
    });

    // GET PRI DETAILS
    builder.addCase(getPRIDetails.pending, state => {
      state.priDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPRIDetails.rejected, (state, action) => {
      state.priDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.priDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getPRIDetails.fulfilled, (state, action) => {
      state.priDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.priDetails = action.payload;
    });

    // IMPORT PRIs
    builder.addCase(postImportPRI.pending, state => {
      state.importPRIPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postImportPRI.rejected, (state, action) => {
      state.importPRIPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postImportPRI.fulfilled, state => {
      state.importPRIPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI imported successfully!');
      });
    });

    // IMPORT DIDs From PRIs
    builder.addCase(postImportDIDsFormPRI.pending, state => {
      state.importDIDsFromPRIPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postImportDIDsFormPRI.rejected, (state, action) => {
      state.importDIDsFromPRIPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postImportDIDsFormPRI.fulfilled, state => {
      state.importDIDsFromPRIPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DIDs imported successfully!');
      });
    });

    // CREATE PRI DETAILS
    builder.addCase(postPRIDetails.pending, state => {
      state.priDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postPRIDetails.rejected, (state, action) => {
      state.priDetailsPostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postPRIDetails.fulfilled, state => {
      state.priDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI created successfully');
      });
    });

    // UPDATE PRI DETAILS
    builder.addCase(patchPRIDetails.pending, state => {
      state.priDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchPRIDetails.rejected, (state, action) => {
      state.priDetailsPatchStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(patchPRIDetails.fulfilled, state => {
      state.priDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI updated successfully');
      });
    });

    // DELETE PRI DETAILS
    builder.addCase(deletePRIDetails.pending, state => {
      state.priDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deletePRIDetails.rejected, (state, action) => {
      state.priDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(deletePRIDetails.fulfilled, state => {
      state.priDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI deleted successfully');
      });
    });

    // PATCH PRI DNIS PREFIX
    builder.addCase(patchPriDnisPrefix.pending, state => {
      state.priDnisPrefixPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchPriDnisPrefix.rejected, (state, action) => {
      state.priDnisPrefixPatchStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload);
    });
    builder.addCase(patchPriDnisPrefix.fulfilled, state => {
      state.priDnisPrefixPatchStatus = FETCH_STATUS.FULFILLED;
      snackbarUtils.success('DNIS Prefix updated successfully!');
    });
  },
});

export const {
  setPriListFetchStatus,
  setPriList,
  setBillingGroupsListFetchStatus,
  setBillingGroupsList,
  setImportPRIPostStatus,
  setPRIDetailsDeleteStatus,
  setPRIDetailsFetchStatus,
  setPRIDetailsPostStatus,
  setPRIDetailsPatchStatus,
  setPRIDetails,
  setRoutesListFetchStatus,
  setRoutesList,
  setImportDIDsFromPRIPostStatus,
  setPriDnisPrefixPatchStatus,
} = csrpSlice.actions;
export default csrpSlice.reducer;
